// src/components/admin/blog/BlogAnalytics.jsx - Complete Updated Version
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiBarChart2,
  FiPieChart,
  FiTrendingUp,
  FiCalendar,
  FiMessageCircle,
  FiEye,
  FiThumbsUp,
  FiStar,
  FiClock,
  FiUsers,
  FiActivity,
  FiAward,
  FiTag,
  FiFolder,
  FiDownload,
  FiRefreshCw,
  FiChevronDown,
  FiChevronUp,
  FiHeart,
  FiSmile,
  FiFrown,
  FiThumbsDown,
  FiZap,
  FiTrendingDown,
  FiBookOpen,
  FiUserCheck,
  FiInfo,
} from 'react-icons/fi';
import { TfiTimer } from 'react-icons/tfi';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  RadialBarChart,
  RadialBar,
  ComposedChart,
  Scatter,
} from 'recharts';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label, isDark, unit = '' }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="p-3 rounded-lg shadow-lg border"
        style={{
          backgroundColor: isDark ? '#1f2937' : '#ffffff',
          borderColor: isDark ? '#374151' : '#e5e7eb',
        }}
      >
        <p
          className="text-sm font-semibold"
          style={{ color: isDark ? '#f1f5f9' : '#1e293b' }}
        >
          {label}
        </p>
        {payload.map((item, index) => (
          <p
            key={index}
            className="text-xs mt-1"
            style={{ color: isDark ? '#94a3b8' : '#64748b' }}
          >
            {item.name}:{' '}
            <span className="font-bold" style={{ color: item.color }}>
              {item.value} {unit}
            </span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Stat Card Component
const StatCard = ({
  title,
  value,
  icon: Icon,
  trend,
  color,
  subtitle,
  tooltip,
}) => {
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl group"
      style={{
        backgroundColor: 'var(--bg)',
        border: '1px solid var(--border)',
      }}
    >
      <div className="absolute top-0 right-0 w-24 h-24 opacity-5 group-hover:opacity-10 transition-opacity">
        <Icon className="w-full h-full" />
      </div>
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <span
              className="text-sm font-medium"
              style={{ color: 'var(--muted)' }}
            >
              {title}
            </span>
            {tooltip && (
              <span
                className="cursor-help text-xs"
                style={{ color: 'var(--muted)' }}
                title={tooltip}
              >
                <FiInfo className="w-3 h-3" />
              </span>
            )}
          </div>
          <div
            className="p-2 rounded-xl"
            style={{ backgroundColor: `${color}15` }}
          >
            <Icon className="w-4 h-4" style={{ color }} />
          </div>
        </div>
        <div
          className="text-3xl font-bold mb-1"
          style={{ color: 'var(--text)' }}
        >
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {trend !== undefined && (
          <div className="flex items-center gap-1 text-xs">
            {trend >= 0 ? (
              <FiTrendingUp className="w-3 h-3 text-green-500" />
            ) : (
              <FiTrendingDown className="w-3 h-3 text-red-500" />
            )}
            <span className={trend >= 0 ? 'text-green-500' : 'text-red-500'}>
              {Math.abs(trend)}%
            </span>
            <span style={{ color: 'var(--muted)' }}>vs last period</span>
          </div>
        )}
        {subtitle && (
          <p className="text-xs mt-2" style={{ color: 'var(--muted)' }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
};

// Format time helper
const formatTime = seconds => {
  if (!seconds || seconds === 0) return '0 min';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

// Main Analytics Component
const BlogAnalytics = ({ blogId = null }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { getAuthToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('30days');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [blogDetailData, setBlogDetailData] = useState(null);
  const [commentAnalytics, setCommentAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Chart colors
  const chartColors = useMemo(
    () => ({
      primary: '#8b5cf6',
      primaryLight: '#a78bfa',
      secondary: '#ec4899',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
      cyan: '#06b6d4',
      purple: '#a855f7',
      orange: '#f97316',
      indigo: '#6366f1',
      reading: '#f472b6',
      text: isDark ? '#f1f5f9' : '#1e293b',
      textSecondary: isDark ? '#94a3b8' : '#64748b',
      grid: isDark ? '#334155' : '#e2e8f0',
    }),
    [isDark],
  );

  const PIE_COLORS = [
    chartColors.primary,
    chartColors.secondary,
    chartColors.warning,
    chartColors.success,
    chartColors.cyan,
    chartColors.purple,
    chartColors.info,
    chartColors.orange,
    chartColors.indigo,
  ];

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAuthToken();
      if (!token) return;

      // Fetch overview analytics
      const overviewResponse = await fetch(
        `/api/blogs?action=analytics-overview&period=${period}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const overviewData = await overviewResponse.json();

      if (overviewData.success) {
        setAnalyticsData(overviewData);
      }

      // Fetch comment analytics if not in blog detail mode
      if (!blogId) {
        const commentsResponse = await fetch(
          `/api/blogs?action=analytics-comments&period=${period}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const commentsData = await commentsResponse.json();
        if (commentsData.success) {
          setCommentAnalytics(commentsData);
        }
      }

      // Fetch blog detail analytics if blogId provided
      if (blogId) {
        const detailResponse = await fetch(
          `/api/blogs?action=analytics-blog-detail&blogId=${blogId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const detailData = await detailResponse.json();
        if (detailData.success) {
          setBlogDetailData(detailData);
        }
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthToken, period, blogId]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  const handleExport = () => {
    const exportData = {
      exportedAt: new Date().toISOString(),
      period,
      overview: analyticsData?.overview,
      distribution: analyticsData?.distribution,
      performance: analyticsData?.performance,
      engagement: analyticsData?.engagement,
      readingStats: analyticsData?.readingStats,
      ...(commentAnalytics && { comments: commentAnalytics }),
      ...(blogDetailData && { blogDetail: blogDetailData }),
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri =
      'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `blog_analytics_${new Date().toISOString().split('T')[0]}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: chartColors.primary }}
        />
      </div>
    );
  }

  const overview = analyticsData?.overview || {};
  const distribution = analyticsData?.distribution || {};
  const performance = analyticsData?.performance || {};
  const engagement = analyticsData?.engagement || {};
  const readingStats = analyticsData?.readingStats || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
            {blogId ? 'Blog Analytics' : 'Blog Management Analytics'}
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
            {blogId
              ? `Detailed analytics for ${blogDetailData?.blog?.title || 'this blog'}`
              : 'Comprehensive insights into your blog performance'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="px-3 py-2 rounded-xl text-sm outline-none cursor-pointer"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
            }}
          >
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="365days">Last Year</option>
          </select>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all hover:opacity-80"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiDownload className="w-4 h-4" />
            <span className="text-sm hidden sm:inline">Export</span>
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiRefreshCw
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
            />
            <span className="text-sm hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Info Banner - Field Definitions */}
      <div
        className="p-4 rounded-xl border text-sm"
        style={{
          borderColor: chartColors.grid,
          backgroundColor: isDark
            ? 'rgba(59, 130, 246, 0.05)'
            : 'rgba(59, 130, 246, 0.05)',
        }}
      >
        <div className="flex flex-wrap items-center gap-4">
          <span className="font-medium" style={{ color: chartColors.text }}>
            📊 Reading Metrics Explained:
          </span>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-blue-500" />
              <span style={{ color: chartColors.textSecondary }}>
                Estimated: from word count
              </span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-pink-500" />
              <span style={{ color: chartColors.textSecondary }}>
                Tracked Avg: from user sessions
              </span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-green-500" />
              <span style={{ color: chartColors.textSecondary }}>
                Total Reads: number of sessions
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      {!blogId && (
        <div
          className="flex gap-2 border-b pb-2 overflow-x-auto"
          style={{ borderColor: 'var(--border)' }}
        >
          {['overview', 'performance', 'engagement', 'reading', 'comments'].map(
            tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize whitespace-nowrap ${
                  activeTab === tab
                    ? 'bg-purple-500/20 text-purple-500'
                    : 'hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                style={{
                  color: activeTab === tab ? undefined : 'var(--muted)',
                }}
              >
                {tab === 'overview' && (
                  <FiBarChart2 className="w-4 h-4 inline mr-2" />
                )}
                {tab === 'performance' && (
                  <FiTrendingUp className="w-4 h-4 inline mr-2" />
                )}
                {tab === 'engagement' && (
                  <FiActivity className="w-4 h-4 inline mr-2" />
                )}
                {tab === 'reading' && (
                  <FiBookOpen className="w-4 h-4 inline mr-2" />
                )}
                {tab === 'comments' && (
                  <FiMessageCircle className="w-4 h-4 inline mr-2" />
                )}
                {tab}
              </button>
            ),
          )}
        </div>
      )}

      {/* Reading Time Tab - FIXED */}
      {(activeTab === 'reading' || (blogId && true)) && (
        <div className="space-y-6">
          {/* Reading Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Reading Time"
              value={
                blogId
                  ? formatTime(blogDetailData?.blog?.totalReadingTime || 0)
                  : formatTime(readingStats.totalReadingTime || 0)
              }
              icon={FiClock}
              color={chartColors.warning}
              subtitle="All sessions combined"
              tooltip="Total reading time across all user sessions"
            />
            <StatCard
              title="Avg Tracked Reading"
              value={
                blogId
                  ? `${blogDetailData?.blog?.avgReadingTime || 0} min`
                  : `${readingStats.averageReadingTime || 0} min`
              }
              icon={TfiTimer}
              color={chartColors.reading}
              subtitle="From user sessions"
              tooltip="Average actual reading time from user sessions"
            />
            <StatCard
              title="Avg Estimated Reading"
              value={
                blogId
                  ? `${blogDetailData?.blog?.readingTime || 0} min`
                  : `${readingStats.averageEstimatedReadingTime || 0} min`
              }
              icon={FiBookOpen}
              color={chartColors.info}
              subtitle="From word count"
              tooltip="Average estimated reading time from word count"
            />
            <StatCard
              title="Total Reads"
              value={
                blogId
                  ? blogDetailData?.blog?.readingCount || 0
                  : readingStats.totalReads || 0
              }
              icon={FiUsers}
              color={chartColors.success}
              subtitle="Reading sessions"
              tooltip="Number of reading sessions"
            />
          </div>

          {/* Estimated vs Tracked Comparison - Individual Blog */}
          {blogId && blogDetailData?.blog && (
            <div
              className="p-5 rounded-2xl border"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <h3
                className="font-semibold flex items-center gap-2 mb-4"
                style={{ color: chartColors.text }}
              >
                <FiBarChart2
                  className="w-4 h-4"
                  style={{ color: chartColors.primary }}
                />
                Estimated vs Tracked Reading Time
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: 'var(--border-light)' }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm" style={{ color: 'var(--muted)' }}>
                        Estimated (from content)
                      </p>
                      <p
                        className="text-3xl font-bold"
                        style={{ color: chartColors.info }}
                      >
                        {blogDetailData.blog.readingTime || 0} min
                      </p>
                    </div>
                    <div
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: `${chartColors.info}15` }}
                    >
                      <FiClock
                        className="w-6 h-6"
                        style={{ color: chartColors.info }}
                      />
                    </div>
                  </div>
                  <p className="text-xs mt-2" style={{ color: 'var(--muted)' }}>
                    Based on {blogDetailData.blog.wordCount || 0} words
                  </p>
                </div>
                <div
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: 'var(--border-light)' }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm" style={{ color: 'var(--muted)' }}>
                        Tracked Average (actual)
                      </p>
                      <p
                        className="text-3xl font-bold"
                        style={{ color: chartColors.reading }}
                      >
                        {blogDetailData.blog.avgReadingTime || 0} min
                      </p>
                    </div>
                    <div
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: `${chartColors.reading}15` }}
                    >
                      <TfiTimer
                        className="w-6 h-6"
                        style={{ color: chartColors.reading }}
                      />
                    </div>
                  </div>
                  <p className="text-xs mt-2" style={{ color: 'var(--muted)' }}>
                    From {blogDetailData.blog.readingCount || 0} reading
                    sessions
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Reading Time Distribution */}
          {(() => {
            // Build chart data
            let chartData = [];

            if (blogId && blogDetailData?.blog) {
              chartData = [
                {
                  name: blogDetailData.blog.title || 'This Blog',
                  estimatedTime: blogDetailData.blog.readingTime || 0,
                  trackedTime: blogDetailData.blog.avgReadingTime || 0,
                  fullTitle: blogDetailData.blog.title || 'This Blog',
                },
              ];
            } else if (
              readingStats.topPerformingByReading &&
              readingStats.topPerformingByReading.length > 0
            ) {
              chartData = readingStats.topPerformingByReading.map(blog => ({
                name:
                  blog.title?.length > 20
                    ? blog.title.substring(0, 20) + '...'
                    : blog.title || 'Unknown',
                estimatedTime: blog.readingTime || 0,
                trackedTime: blog.avgReadingTime || 0,
                fullTitle: blog.title || 'Unknown',
              }));
            } else if (
              performance.topPerforming &&
              performance.topPerforming.length > 0
            ) {
              chartData = performance.topPerforming.map(blog => ({
                name:
                  blog.title?.length > 20
                    ? blog.title.substring(0, 20) + '...'
                    : blog.title || 'Unknown',
                estimatedTime: blog.readingTime || 0,
                trackedTime: blog.avgReadingTime || 0,
                fullTitle: blog.title || 'Unknown',
              }));
            }

            if (chartData.length === 0) {
              return (
                <div
                  className="p-8 rounded-2xl border text-center"
                  style={{
                    backgroundColor: 'var(--bg)',
                    borderColor: 'var(--border)',
                  }}
                >
                  <FiClock
                    className="w-12 h-12 mx-auto mb-3"
                    style={{ color: 'var(--muted)' }}
                  />
                  <p className="text-sm" style={{ color: 'var(--muted)' }}>
                    No reading time data available yet.
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                    Start tracking reading times to see analytics here.
                  </p>
                </div>
              );
            }

            return (
              <div
                className="p-5 rounded-2xl border"
                style={{
                  backgroundColor: 'var(--bg)',
                  borderColor: 'var(--border)',
                }}
              >
                <h3
                  className="font-semibold flex items-center gap-2 mb-4"
                  style={{ color: chartColors.text }}
                >
                  <FiClock
                    className="w-4 h-4"
                    style={{ color: chartColors.reading }}
                  />
                  {blogId
                    ? 'Reading Time Breakdown'
                    : 'Reading Time Distribution'}
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={chartColors.grid}
                      />
                      <XAxis
                        dataKey="name"
                        tick={{ fill: chartColors.textSecondary, fontSize: 10 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis
                        tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                        label={{
                          value: 'Reading Time (minutes)',
                          angle: -90,
                          position: 'insideLeft',
                          style: {
                            fill: chartColors.textSecondary,
                            fontSize: 11,
                          },
                        }}
                      />
                      <Tooltip
                        content={<CustomTooltip isDark={isDark} unit="min" />}
                        labelFormatter={(label, items) => {
                          const item = items[0];
                          if (item && item.payload) {
                            return item.payload.fullTitle || label;
                          }
                          return label;
                        }}
                      />
                      <Legend />
                      <Bar
                        dataKey="estimatedTime"
                        name="Estimated"
                        fill={chartColors.info}
                        radius={[8, 8, 0, 0]}
                      />
                      <Bar
                        dataKey="trackedTime"
                        name="Tracked Avg"
                        fill={chartColors.reading}
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })()}

          {/* Reading Insights */}
          <div
            className="p-5 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <h3
              className="font-semibold flex items-center gap-2 mb-4"
              style={{ color: chartColors.text }}
            >
              <FiZap
                className="w-4 h-4"
                style={{ color: chartColors.warning }}
              />
              Reading Insights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Most Engaged */}
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: 'var(--border-light)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                    <FiTrendingUp className="w-4 h-4 text-green-500" />
                  </div>
                  <span
                    className="text-sm font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    Most Engaged
                  </span>
                </div>
                {(() => {
                  let mostEngaged = null;

                  if (blogId && blogDetailData?.blog) {
                    mostEngaged = blogDetailData.blog;
                  } else if (
                    readingStats.topPerformingByReading &&
                    readingStats.topPerformingByReading.length > 0
                  ) {
                    mostEngaged = readingStats.topPerformingByReading[0];
                  } else if (
                    performance.topPerforming &&
                    performance.topPerforming.length > 0
                  ) {
                    mostEngaged = performance.topPerforming[0];
                  }

                  if (mostEngaged) {
                    return (
                      <div>
                        <p className="text-sm" style={{ color: 'var(--text)' }}>
                          {mostEngaged.title || 'Unknown'}
                        </p>
                        <div className="flex gap-3 text-xs mt-1">
                          <span style={{ color: chartColors.info }}>
                            📖 {mostEngaged.readingTime || 0}min est
                          </span>
                          <span style={{ color: chartColors.reading }}>
                            ⏱️ {mostEngaged.avgReadingTime || 0}min avg
                          </span>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <p className="text-sm" style={{ color: 'var(--muted)' }}>
                        No data available
                      </p>
                    );
                  }
                })()}
              </div>

              {/* Gap Analysis */}
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: 'var(--border-light)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <FiBarChart2 className="w-4 h-4 text-blue-500" />
                  </div>
                  <span
                    className="text-sm font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    Gap Analysis
                  </span>
                </div>
                {blogId && blogDetailData?.blog ? (
                  <div>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      {blogDetailData.blog.avgReadingTime >
                      blogDetailData.blog.readingTime
                        ? '✅ Users read longer than estimated'
                        : blogDetailData.blog.avgReadingTime <
                            blogDetailData.blog.readingTime
                          ? '⚠️ Users read shorter than estimated'
                          : '✓ Match'}
                    </p>
                    <div className="flex gap-3 text-xs mt-1">
                      <span style={{ color: chartColors.info }}>
                        Est: {blogDetailData.blog.readingTime || 0}min
                      </span>
                      <span style={{ color: chartColors.reading }}>
                        Tracked: {blogDetailData.blog.avgReadingTime || 0}min
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: 'var(--muted)' }}>
                    Select a blog to see gap analysis
                  </p>
                )}
              </div>

              {/* Reading Engagement */}
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: 'var(--border-light)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <FiUsers className="w-4 h-4 text-purple-500" />
                  </div>
                  <span
                    className="text-sm font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    Reading Engagement
                  </span>
                </div>
                <div>
                  <p
                    className="text-lg font-bold"
                    style={{ color: 'var(--text)' }}
                  >
                    {blogId
                      ? `${blogDetailData?.blog?.readingCount || 0}`
                      : `${readingStats.totalReads || 0}`}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--muted)' }}>
                    Reading sessions
                  </p>
                  {blogId && blogDetailData?.blog && (
                    <p
                      className="text-xs mt-1"
                      style={{ color: 'var(--muted)' }}
                    >
                      Avg:{' '}
                      {Math.round(
                        (blogDetailData.blog.totalReadingTime || 0) /
                          Math.max(blogDetailData.blog.readingCount || 1, 1),
                      )}
                      s per read
                    </p>
                  )}
                  {!blogId &&
                    readingStats.totalReads > 0 &&
                    readingStats.totalReadingTime > 0 && (
                      <p
                        className="text-xs mt-1"
                        style={{ color: 'var(--muted)' }}
                      >
                        Avg:{' '}
                        {Math.round(
                          (readingStats.totalReadingTime || 0) /
                            Math.max(readingStats.totalReads || 1, 1) /
                            60,
                        )}{' '}
                        min per read
                      </p>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overview Tab */}
      {(activeTab === 'overview' || (blogId && activeTab === 'overview')) && (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <StatCard
              title="Total Blogs"
              value={blogId ? 1 : overview.totalBlogs || 0}
              icon={FiFolder}
              color={chartColors.primary}
            />
            <StatCard
              title="Total Views"
              value={
                blogId
                  ? blogDetailData?.blog?.views || 0
                  : overview.totalViews || 0
              }
              icon={FiEye}
              color={chartColors.info}
            />
            <StatCard
              title="Total Comments"
              value={
                blogId
                  ? blogDetailData?.comments?.total || 0
                  : overview.totalComments || 0
              }
              icon={FiMessageCircle}
              color={chartColors.warning}
            />
            <StatCard
              title="Total Reactions"
              value={
                blogId
                  ? blogDetailData?.comments?.totalReactions || 0
                  : overview.totalReactions || 0
              }
              icon={FiHeart}
              color={chartColors.secondary}
            />
            <StatCard
              title="Engagement Rate"
              value={
                blogId
                  ? `${blogDetailData?.performance?.engagementRate || 0}%`
                  : `${overview.engagementRate || 0}%`
              }
              icon={FiActivity}
              color={chartColors.success}
            />
            <StatCard
              title="Avg Tracked Reading"
              value={
                blogId
                  ? `${blogDetailData?.blog?.avgReadingTime || 0} min`
                  : `${readingStats.averageReadingTime || 0} min`
              }
              icon={TfiTimer}
              color={chartColors.cyan}
              tooltip="Average actual reading time from user sessions"
            />
          </div>

          {/* Category & Status Distribution */}
          {!blogId && distribution.categories?.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Category Distribution */}
              <div
                className="p-5 rounded-2xl border"
                style={{
                  backgroundColor: 'var(--bg)',
                  borderColor: 'var(--border)',
                }}
              >
                <h3
                  className="font-semibold flex items-center gap-2 mb-4"
                  style={{ color: chartColors.text }}
                >
                  <FiTag
                    className="w-4 h-4"
                    style={{ color: chartColors.primary }}
                  />
                  Posts by Category
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={distribution.categories}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="count"
                        nameKey="name"
                        label={({ name, percent }) =>
                          `${name} (${(percent * 100).toFixed(0)}%)`
                        }
                        labelLine={false}
                      >
                        {distribution.categories.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={PIE_COLORS[index % PIE_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: isDark ? '#1f2937' : '#ffffff',
                          borderColor: isDark ? '#374151' : '#e5e7eb',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend
                        formatter={value => (
                          <span
                            style={{
                              color: chartColors.textSecondary,
                              fontSize: '11px',
                            }}
                          >
                            {value}
                          </span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Status Distribution */}
              <div
                className="p-5 rounded-2xl border"
                style={{
                  backgroundColor: 'var(--bg)',
                  borderColor: 'var(--border)',
                }}
              >
                <h3
                  className="font-semibold flex items-center gap-2 mb-4"
                  style={{ color: chartColors.text }}
                >
                  <FiPieChart
                    className="w-4 h-4"
                    style={{ color: chartColors.secondary }}
                  />
                  Posts by Status
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          {
                            name: 'Published',
                            value: distribution.status?.published || 0,
                            color: chartColors.success,
                          },
                          {
                            name: 'Draft',
                            value: distribution.status?.draft || 0,
                            color: chartColors.warning,
                          },
                          {
                            name: 'Scheduled',
                            value: distribution.status?.scheduled || 0,
                            color: chartColors.cyan,
                          },
                          {
                            name: 'Archived',
                            value: distribution.status?.archived || 0,
                            color: chartColors.error,
                          },
                        ].filter(d => d.value > 0)}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                      >
                        {[
                          chartColors.success,
                          chartColors.warning,
                          chartColors.cyan,
                          chartColors.error,
                        ].map((color, index) => (
                          <Cell key={`cell-${index}`} fill={color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: isDark ? '#1f2937' : '#ffffff',
                          borderColor: isDark ? '#374151' : '#e5e7eb',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend
                        formatter={value => (
                          <span
                            style={{
                              color: chartColors.textSecondary,
                              fontSize: '11px',
                            }}
                          >
                            {value}
                          </span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* Comments & Reactions Over Time */}
          {engagement.commentsOverTime?.length > 0 && (
            <div
              className="p-5 rounded-2xl border"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <h3
                className="font-semibold flex items-center gap-2 mb-4"
                style={{ color: chartColors.text }}
              >
                <FiCalendar
                  className="w-4 h-4"
                  style={{ color: chartColors.cyan }}
                />
                Comments & Engagement Over Time
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={engagement.commentsOverTime}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={chartColors.grid}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                      interval={
                        period === '30days' ? 5 : period === '90days' ? 14 : 30
                      }
                    />
                    <YAxis
                      yAxisId="left"
                      tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                    />
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                    <Legend
                      wrapperStyle={{ color: chartColors.textSecondary }}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="count"
                      name="Comments"
                      fill={chartColors.warning}
                      radius={[8, 8, 0, 0]}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="views"
                      name="Views"
                      stroke={chartColors.info}
                      strokeWidth={2}
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Reaction Distribution */}
          {engagement.reactionDistribution &&
            Object.values(engagement.reactionDistribution).some(v => v > 0) && (
              <div
                className="p-5 rounded-2xl border"
                style={{
                  backgroundColor: 'var(--bg)',
                  borderColor: 'var(--border)',
                }}
              >
                <h3
                  className="font-semibold flex items-center gap-2 mb-4"
                  style={{ color: chartColors.text }}
                >
                  <FiHeart
                    className="w-4 h-4"
                    style={{ color: chartColors.secondary }}
                  />
                  Reaction Distribution
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={Object.entries(engagement.reactionDistribution)
                            .map(([key, value]) => ({
                              name: key.charAt(0).toUpperCase() + key.slice(1),
                              value,
                              emoji:
                                key === 'like'
                                  ? '👍'
                                  : key === 'love'
                                    ? '❤️'
                                    : key === 'haha'
                                      ? '😂'
                                      : key === 'sad'
                                        ? '😢'
                                        : '😠',
                            }))
                            .filter(d => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          nameKey="name"
                          label={({ name, percent }) =>
                            `${name} (${(percent * 100).toFixed(0)}%)`
                          }
                          labelLine={false}
                        >
                          {Object.keys(engagement.reactionDistribution)
                            .filter(k => engagement.reactionDistribution[k] > 0)
                            .map((_, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={PIE_COLORS[index % PIE_COLORS.length]}
                              />
                            ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDark ? '#1f2937' : '#ffffff',
                            borderColor: isDark ? '#374151' : '#e5e7eb',
                            borderRadius: '8px',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-3">
                    {Object.entries(engagement.reactionDistribution).map(
                      ([key, value]) => {
                        if (value === 0) return null;
                        const total = Object.values(
                          engagement.reactionDistribution,
                        ).reduce((a, b) => a + b, 0);
                        const percentage =
                          total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        const emoji =
                          key === 'like'
                            ? '👍'
                            : key === 'love'
                              ? '❤️'
                              : key === 'haha'
                                ? '😂'
                                : key === 'sad'
                                  ? '😢'
                                  : '😠';
                        return (
                          <div key={key} className="flex items-center gap-3">
                            <div className="w-8 text-xl">{emoji}</div>
                            <div className="flex-1">
                              <div className="flex justify-between text-sm mb-1">
                                <span style={{ color: chartColors.text }}>
                                  {key.charAt(0).toUpperCase() + key.slice(1)}
                                </span>
                                <span
                                  style={{ color: chartColors.textSecondary }}
                                >
                                  {value.toLocaleString()}
                                </span>
                              </div>
                              <div
                                className="h-2 rounded-full overflow-hidden"
                                style={{ backgroundColor: chartColors.grid }}
                              >
                                <div
                                  className="h-full rounded-full transition-all duration-500"
                                  style={{
                                    width: `${percentage}%`,
                                    backgroundColor:
                                      PIE_COLORS[
                                        Object.keys(
                                          engagement.reactionDistribution,
                                        ).indexOf(key) % PIE_COLORS.length
                                      ],
                                  }}
                                />
                              </div>
                            </div>
                            <span
                              className="text-sm"
                              style={{ color: chartColors.textSecondary }}
                            >
                              {percentage}%
                            </span>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              </div>
            )}

          {/* Top Performing Blogs */}
          {performance.topPerforming?.length > 0 && !blogId && (
            <div
              className="p-5 rounded-2xl border"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <h3
                className="font-semibold flex items-center gap-2 mb-4"
                style={{ color: chartColors.text }}
              >
                <FiAward
                  className="w-4 h-4"
                  style={{ color: chartColors.warning }}
                />
                Top Performing Blogs
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr
                      style={{ borderBottom: `1px solid ${chartColors.grid}` }}
                    >
                      <th
                        className="text-left py-3 text-sm font-medium"
                        style={{ color: chartColors.textSecondary }}
                      >
                        Title
                      </th>
                      <th
                        className="text-center py-3 text-sm font-medium"
                        style={{ color: chartColors.textSecondary }}
                      >
                        Views
                      </th>
                      <th
                        className="text-center py-3 text-sm font-medium"
                        style={{ color: chartColors.textSecondary }}
                      >
                        Likes
                      </th>
                      <th
                        className="text-center py-3 text-sm font-medium"
                        style={{ color: chartColors.textSecondary }}
                      >
                        Est. Time
                      </th>
                      <th
                        className="text-center py-3 text-sm font-medium"
                        style={{ color: chartColors.textSecondary }}
                      >
                        Tracked Avg
                      </th>
                      <th
                        className="text-center py-3 text-sm font-medium"
                        style={{ color: chartColors.textSecondary }}
                      >
                        Reads
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {performance.topPerforming.map((blog, index) => (
                      <tr
                        key={blog.id}
                        style={{
                          borderBottom: `1px solid ${chartColors.grid}`,
                        }}
                      >
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <span
                              className="text-sm font-medium"
                              style={{ color: chartColors.text }}
                            >
                              {index + 1}.
                            </span>
                            <span
                              className="text-sm"
                              style={{ color: chartColors.text }}
                            >
                              {blog.title}
                            </span>
                          </div>
                        </td>
                        <td
                          className="text-center text-sm"
                          style={{ color: chartColors.info }}
                        >
                          {blog.views.toLocaleString()}
                        </td>
                        <td
                          className="text-center text-sm"
                          style={{ color: chartColors.secondary }}
                        >
                          {blog.likes.toLocaleString()}
                        </td>
                        <td
                          className="text-center text-sm"
                          style={{ color: chartColors.cyan }}
                        >
                          {blog.readingTime || 0} min
                        </td>
                        <td
                          className="text-center text-sm"
                          style={{ color: chartColors.reading }}
                        >
                          {blog.avgReadingTime || 0} min
                        </td>
                        <td
                          className="text-center text-sm"
                          style={{ color: chartColors.success }}
                        >
                          {blog.readingCount || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Top Commenters */}
          {engagement.topCommenters?.length > 0 && !blogId && (
            <div
              className="p-5 rounded-2xl border"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <h3
                className="font-semibold flex items-center gap-2 mb-4"
                style={{ color: chartColors.text }}
              >
                <FiUsers
                  className="w-4 h-4"
                  style={{ color: chartColors.success }}
                />
                Most Active Commenters
              </h3>
              <div className="space-y-3">
                {engagement.topCommenters.map((commenter, index) => (
                  <div
                    key={commenter.email}
                    className="flex items-center gap-3 p-3 rounded-xl transition-all hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                      {commenter.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="flex-1">
                      <p
                        className="text-sm font-medium"
                        style={{ color: chartColors.text }}
                      >
                        {commenter.name}
                      </p>
                      <p
                        className="text-xs"
                        style={{ color: chartColors.textSecondary }}
                      >
                        {commenter.email}
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className="text-lg font-bold"
                        style={{ color: chartColors.warning }}
                      >
                        {commenter.count}
                      </p>
                      <p
                        className="text-xs"
                        style={{ color: chartColors.textSecondary }}
                      >
                        comments
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' &&
        performance.weeklyPerformance?.length > 0 &&
        !blogId && (
          <div className="space-y-6">
            {/* Weekly Performance */}
            <div
              className="p-5 rounded-2xl border"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <h3
                className="font-semibold flex items-center gap-2 mb-4"
                style={{ color: chartColors.text }}
              >
                <FiTrendingUp
                  className="w-4 h-4"
                  style={{ color: chartColors.primary }}
                />
                Weekly Performance
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={performance.weeklyPerformance}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={chartColors.grid}
                    />
                    <XAxis
                      dataKey="week"
                      tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                    />
                    <YAxis
                      yAxisId="left"
                      tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                    />
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                    <Legend
                      wrapperStyle={{ color: chartColors.textSecondary }}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="posts"
                      name="New Posts"
                      fill={chartColors.primary}
                      radius={[8, 8, 0, 0]}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="comments"
                      name="Comments"
                      fill={chartColors.warning}
                      radius={[8, 8, 0, 0]}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="views"
                      name="Views"
                      stroke={chartColors.info}
                      strokeWidth={2}
                      dot={{ fill: chartColors.info, r: 4 }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Category Performance */}
            {performance.categoryPerformance?.length > 0 && (
              <div
                className="p-5 rounded-2xl border"
                style={{
                  backgroundColor: 'var(--bg)',
                  borderColor: 'var(--border)',
                }}
              >
                <h3
                  className="font-semibold flex items-center gap-2 mb-4"
                  style={{ color: chartColors.text }}
                >
                  <FiFolder
                    className="w-4 h-4"
                    style={{ color: chartColors.cyan }}
                  />
                  Category Performance
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr
                        style={{
                          borderBottom: `1px solid ${chartColors.grid}`,
                        }}
                      >
                        <th
                          className="text-left py-3 text-sm font-medium"
                          style={{ color: chartColors.textSecondary }}
                        >
                          Category
                        </th>
                        <th
                          className="text-center py-3 text-sm font-medium"
                          style={{ color: chartColors.textSecondary }}
                        >
                          Posts
                        </th>
                        <th
                          className="text-center py-3 text-sm font-medium"
                          style={{ color: chartColors.textSecondary }}
                        >
                          Total Views
                        </th>
                        <th
                          className="text-center py-3 text-sm font-medium"
                          style={{ color: chartColors.textSecondary }}
                        >
                          Comments
                        </th>
                        <th
                          className="text-center py-3 text-sm font-medium"
                          style={{ color: chartColors.textSecondary }}
                        >
                          Avg Views/Post
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {performance.categoryPerformance.map(
                        (category, index) => (
                          <tr
                            key={category.name}
                            style={{
                              borderBottom: `1px solid ${chartColors.grid}`,
                            }}
                          >
                            <td className="py-3">
                              <span
                                className="text-sm font-medium"
                                style={{ color: chartColors.text }}
                              >
                                {category.name}
                              </span>
                            </td>
                            <td
                              className="text-center text-sm"
                              style={{ color: chartColors.textSecondary }}
                            >
                              {category.postCount}
                            </td>
                            <td
                              className="text-center text-sm"
                              style={{ color: chartColors.info }}
                            >
                              {category.views.toLocaleString()}
                            </td>
                            <td
                              className="text-center text-sm"
                              style={{ color: chartColors.warning }}
                            >
                              {category.comments.toLocaleString()}
                            </td>
                            <td
                              className="text-center text-sm"
                              style={{ color: chartColors.textSecondary }}
                            >
                              {Math.round(
                                category.views / category.postCount,
                              ).toLocaleString()}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

      {/* Engagement Tab */}
      {activeTab === 'engagement' && !blogId && (
        <div className="space-y-6">
          {/* Comments Timeline */}
          <div
            className="p-5 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <h3
              className="font-semibold flex items-center gap-2 mb-4"
              style={{ color: chartColors.text }}
            >
              <FiMessageCircle
                className="w-4 h-4"
                style={{ color: chartColors.warning }}
              />
              Comments Timeline
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={engagement.commentsOverTime}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="commentGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={chartColors.warning}
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor={chartColors.warning}
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={chartColors.grid}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                    interval={
                      period === '30days' ? 5 : period === '90days' ? 14 : 30
                    }
                  />
                  <YAxis
                    tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                    allowDecimals={false}
                  />
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    name="Comments"
                    stroke={chartColors.warning}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#commentGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Reaction Distribution */}
          {engagement.reactionDistribution &&
            Object.values(engagement.reactionDistribution).some(v => v > 0) && (
              <div
                className="p-5 rounded-2xl border"
                style={{
                  backgroundColor: 'var(--bg)',
                  borderColor: 'var(--border)',
                }}
              >
                <h3
                  className="font-semibold flex items-center gap-2 mb-4"
                  style={{ color: chartColors.text }}
                >
                  <FiHeart
                    className="w-4 h-4"
                    style={{ color: chartColors.secondary }}
                  />
                  Reaction Distribution
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadialBarChart
                        data={Object.entries(engagement.reactionDistribution)
                          .map(([key, value]) => ({
                            name: key.charAt(0).toUpperCase() + key.slice(1),
                            value,
                            fill:
                              key === 'like'
                                ? chartColors.info
                                : key === 'love'
                                  ? chartColors.secondary
                                  : key === 'haha'
                                    ? chartColors.warning
                                    : key === 'sad'
                                      ? chartColors.primary
                                      : chartColors.error,
                          }))
                          .filter(d => d.value > 0)}
                        innerRadius="20%"
                        outerRadius="80%"
                        startAngle={90}
                        endAngle={-270}
                      >
                        <RadialBar
                          minAngle={15}
                          background
                          clockWise
                          dataKey="value"
                          cornerRadius={10}
                        />
                        <Legend
                          iconSize={10}
                          width={120}
                          height={140}
                          layout="vertical"
                          verticalAlign="middle"
                          align="right"
                          formatter={value => (
                            <span
                              style={{
                                color: chartColors.textSecondary,
                                fontSize: '11px',
                              }}
                            >
                              {value}
                            </span>
                          )}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDark ? '#1f2937' : '#ffffff',
                            borderColor: isDark ? '#374151' : '#e5e7eb',
                            borderRadius: '8px',
                          }}
                        />
                      </RadialBarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-3">
                    {Object.entries(engagement.reactionDistribution).map(
                      ([key, value]) => {
                        if (value === 0) return null;
                        const total = Object.values(
                          engagement.reactionDistribution,
                        ).reduce((a, b) => a + b, 0);
                        const percentage =
                          total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        const emoji =
                          key === 'like'
                            ? '👍'
                            : key === 'love'
                              ? '❤️'
                              : key === 'haha'
                                ? '😂'
                                : key === 'sad'
                                  ? '😢'
                                  : '😠';
                        return (
                          <div key={key} className="flex items-center gap-3">
                            <div className="w-8 text-xl">{emoji}</div>
                            <div className="flex-1">
                              <div className="flex justify-between text-sm mb-1">
                                <span style={{ color: chartColors.text }}>
                                  {key.charAt(0).toUpperCase() + key.slice(1)}
                                </span>
                                <span
                                  style={{ color: chartColors.textSecondary }}
                                >
                                  {value.toLocaleString()}
                                </span>
                              </div>
                              <div
                                className="h-2 rounded-full overflow-hidden"
                                style={{ backgroundColor: chartColors.grid }}
                              >
                                <div
                                  className="h-full rounded-full transition-all duration-500"
                                  style={{
                                    width: `${percentage}%`,
                                    backgroundColor:
                                      key === 'like'
                                        ? chartColors.info
                                        : key === 'love'
                                          ? chartColors.secondary
                                          : key === 'haha'
                                            ? chartColors.warning
                                            : key === 'sad'
                                              ? chartColors.primary
                                              : chartColors.error,
                                  }}
                                />
                              </div>
                            </div>
                            <span
                              className="text-sm"
                              style={{ color: chartColors.textSecondary }}
                            >
                              {percentage}%
                            </span>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              </div>
            )}

          {/* Top Commenters */}
          {engagement.topCommenters?.length > 0 && (
            <div
              className="p-5 rounded-2xl border"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <h3
                className="font-semibold flex items-center gap-2 mb-4"
                style={{ color: chartColors.text }}
              >
                <FiUsers
                  className="w-4 h-4"
                  style={{ color: chartColors.success }}
                />
                Most Active Commenters
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {engagement.topCommenters.map((commenter, index) => (
                  <div
                    key={commenter.email}
                    className="flex items-center gap-3 p-3 rounded-xl transition-all hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                      {commenter.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="flex-1">
                      <p
                        className="text-sm font-medium"
                        style={{ color: chartColors.text }}
                      >
                        {commenter.name}
                      </p>
                      <p
                        className="text-xs"
                        style={{ color: chartColors.textSecondary }}
                      >
                        {commenter.email}
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className="text-lg font-bold"
                        style={{ color: chartColors.warning }}
                      >
                        {commenter.count}
                      </p>
                      <p
                        className="text-xs"
                        style={{ color: chartColors.textSecondary }}
                      >
                        comments
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Comments Tab */}
      {activeTab === 'comments' && commentAnalytics && !blogId && (
        <div className="space-y-6">
          {/* Comments Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Comments"
              value={commentAnalytics.overview?.totalComments || 0}
              icon={FiMessageCircle}
              color={chartColors.warning}
            />
            <StatCard
              title="Total Reactions"
              value={commentAnalytics.overview?.totalReactions || 0}
              icon={FiHeart}
              color={chartColors.secondary}
            />
            <StatCard
              title="Avg Reactions/Comment"
              value={commentAnalytics.overview?.averageReactionsPerComment || 0}
              icon={FiActivity}
              color={chartColors.cyan}
            />
            <StatCard
              title="Active Commenters"
              value={commentAnalytics.overview?.activeCommenters || 0}
              icon={FiUsers}
              color={chartColors.success}
            />
          </div>

          {/* Daily Comment Activity */}
          {commentAnalytics.trends?.dailyActivity?.length > 0 && (
            <div
              className="p-5 rounded-2xl border"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <h3
                className="font-semibold flex items-center gap-2 mb-4"
                style={{ color: chartColors.text }}
              >
                <FiActivity
                  className="w-4 h-4"
                  style={{ color: chartColors.cyan }}
                />
                Daily Comment Activity
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={commentAnalytics.trends.dailyActivity}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={chartColors.grid}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                      interval={period === '30days' ? 5 : 14}
                    />
                    <YAxis
                      yAxisId="left"
                      tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                    />
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="comments"
                      name="Comments"
                      fill={chartColors.warning}
                      radius={[8, 8, 0, 0]}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="reactions"
                      name="Reactions"
                      stroke={chartColors.secondary}
                      strokeWidth={2}
                      dot={{ fill: chartColors.secondary, r: 4 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Hourly Distribution */}
          {commentAnalytics.distribution?.hourlyDistribution && (
            <div
              className="p-5 rounded-2xl border"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <h3
                className="font-semibold flex items-center gap-2 mb-4"
                style={{ color: chartColors.text }}
              >
                <FiClock
                  className="w-4 h-4"
                  style={{ color: chartColors.info }}
                />
                Comments by Hour
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={commentAnalytics.distribution.hourlyDistribution.map(
                      (count, hour) => ({
                        hour: `${hour}:00`,
                        count,
                      }),
                    )}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={chartColors.grid}
                    />
                    <XAxis
                      dataKey="hour"
                      tick={{ fill: chartColors.textSecondary, fontSize: 10 }}
                      interval={3}
                    />
                    <YAxis
                      tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                    />
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                    <Bar
                      dataKey="count"
                      name="Comments"
                      fill={chartColors.primary}
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Top Commented Blogs */}
          {commentAnalytics.trends?.topCommentedBlogs?.length > 0 && (
            <div
              className="p-5 rounded-2xl border"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <h3
                className="font-semibold flex items-center gap-2 mb-4"
                style={{ color: chartColors.text }}
              >
                <FiFolder
                  className="w-4 h-4"
                  style={{ color: chartColors.warning }}
                />
                Most Commented Blogs
              </h3>
              <div className="space-y-3">
                {commentAnalytics.trends.topCommentedBlogs.map(
                  (blog, index) => (
                    <div
                      key={blog.blogId}
                      className="flex items-center justify-between p-3 rounded-xl transition-all hover:bg-black/5 dark:hover:bg-white/5"
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className="text-sm font-bold"
                          style={{ color: chartColors.warning }}
                        >
                          #{index + 1}
                        </span>
                        <div>
                          <p
                            className="text-sm font-medium"
                            style={{ color: chartColors.text }}
                          >
                            {blog.title}
                          </p>
                          <p
                            className="text-xs"
                            style={{ color: chartColors.textSecondary }}
                          >
                            Last activity:{' '}
                            {new Date(blog.recentComment).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p
                          className="text-lg font-bold"
                          style={{ color: chartColors.warning }}
                        >
                          {blog.count}
                        </p>
                        <p
                          className="text-xs"
                          style={{ color: chartColors.textSecondary }}
                        >
                          comments
                        </p>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogAnalytics;
